<template>
  <div class="shop-info-page">
    <!-- 顶部导航 -->
    <header class="back-header">
      <div class="back-btn" @click="goBack">←</div>
      <h1>{{ $t('wholesaler.ShopInfo.line6_text') }}</h1>
      <div style="flex: 1;"></div>
    </header>

    <!-- 页面内容 -->
    <div class="page-content">
      <!-- 基本信息 -->
      <div class="info-section">
        <h3>{{ $t('wholesaler.ShopInfo.line14_text') }}</h3>
        
        <div class="form-item">
          <label>{{ $t('wholesaler.ShopInfo.line17_text') }}</label>
          <input 
            type="text" 
            v-model="formData.companyName"
            :placeholder="$t('wholesaler.ShopInfo.line21_placeholder')"
          />
        </div>

        <div class="form-item">
          <label>{{ $t('wholesaler.ShopInfo.line26_text') }}</label>
          <textarea 
            v-model="formData.description" 
            :placeholder="$t('wholesaler.ShopInfo.line29_placeholder')"
            rows="4"
          ></textarea>
        </div>

        <!-- ✅ 店铺头像显示区域 -->
        <div class="form-item avatar-upload">
          <label>{{ $t('wholesaler.ShopInfo.line36_text') }}</label>
          <div class="avatar-container" @click="triggerAvatarUpload">
            <!-- ✅ 有头像时显示图片 -->
            <img 
              v-if="formData.avatarUrl" 
              :src="getImageUrl(formData.avatarUrl)" 
              :alt="$t('wholesaler.ShopInfo.line42_alt')" 
              @error="handleImageError"
            />
            <!-- 没有头像时显示占位符 -->
            <div v-else class="avatar-placeholder">
              <span class="avatar-icon">📷</span>
              <p class="upload-text">{{ $t('wholesaler.ShopInfo.line48_text') }}</p>
            </div>
          </div>
          <input 
            ref="avatarInput"
            type="file" 
            accept="image/*" 
            style="display: none;" 
            @change="handleAvatarUpload"
          />
        </div>


      </div>

      <!-- ⭐ PayNow 收款码 -->
      <div class="info-section paynow-section">
        <h3>{{ $t('wholesaler.ShopInfo.line63_text') }}</h3>
        <p class="section-hint">{{ $t('wholesaler.ShopInfo.line64_text') }}</p>
        
        <div class="form-item paynow-upload">
          <div class="paynow-container" @click="triggerPayNowUpload">
            <img 
              v-if="formData.paynowQrUrl" 
              :src="getImageUrl(formData.paynowQrUrl)" 
              alt="PayNow QR Code" 
              class="paynow-qr-image"
            />
            <div v-else class="paynow-placeholder">
              <span class="paynow-icon">📱</span>
              <p class="upload-text">{{ $t('wholesaler.ShopInfo.line76_text') }}</p>
              <p class="upload-hint">{{ $t('wholesaler.ShopInfo.line77_text') }}</p>
            </div>
          </div>
          <input 
            ref="paynowInput"
            type="file" 
            accept="image/*" 
            style="display: none;" 
            @change="handlePayNowUpload"
          />
        </div>
      </div>

      <!-- 联系方式 -->
      <div class="info-section">
        <h3>{{ $t('wholesaler.ShopInfo.line92_text') }}</h3>
        
        <div class="form-item">
          <label>{{ $t('wholesaler.ShopInfo.line95_text') }}</label>
          <input 
            type="text" 
            v-model="formData.contactPerson" 
            :placeholder="$t('wholesaler.ShopInfo.line99_placeholder')"
          />
        </div>

        <div class="form-item">
          <label>{{ $t('wholesaler.ShopInfo.line104_text') }}</label>
          <input 
            type="tel" 
            v-model="formData.contactPhone" 
            :placeholder="$t('wholesaler.ShopInfo.line108_placeholder')"
          />
        </div>

        <div class="form-item">
          <label>{{ $t('wholesaler.ShopInfo.line113_text') }}</label>
          <input 
            type="text" 
            v-model="formData.whatsappNumber" 
            :placeholder="$t('wholesaler.ShopInfo.line117_placeholder')"
          />
        </div>

        <div class="form-item">
          <label>{{ $t('wholesaler.ShopInfo.line122_text') }}</label>
          <div class="address-input-row">
            <input 
              type="text" 
              v-model="formData.address" 
              :placeholder="$t('wholesaler.ShopInfo.line127_placeholder')"
            />
            <button type="button" class="btn-map-pick" @click="showMapPicker = true">
              📍 {{ $t('wholesaler.ShopInfo.mapPick') }}
            </button>
          </div>
        </div>
      </div>

      <!-- 营业信息 -->
      <div class="info-section">
        <h3>{{ $t('wholesaler.ShopInfo.line138_text') }}</h3>
        
        <div class="form-item">
          <label>{{ $t('wholesaler.ShopInfo.line141_text') }}</label>
          <input 
            type="text" 
            v-model="formData.businessLicenseNo" 
            :placeholder="$t('wholesaler.ShopInfo.line145_placeholder')"
          />
        </div>

        <div class="form-item">
          <label>{{ $t('wholesaler.ShopInfo.line150_text') }}</label>
          <input 
            type="text" 
            v-model="formData.wholesalerLevel" 
            :placeholder="$t('wholesaler.ShopInfo.line154_placeholder')"
          />
        </div>

        <!-- ⭐ 零负库存下单开关 -->
        <div class="form-item switch-item">
          <label>{{ $t('wholesaler.ShopInfo.line160_text') }}</label>
          <div class="switch-container">
            <label class="switch">
              <input 
                type="checkbox" 
                v-model="formData.allowNegativeStock"
                @change="toggleNegativeStock"
              />
              <span class="switch-slider"></span>
            </label>
            <span class="switch-label">{{ formData.allowNegativeStock ? $t('wholesaler.ShopInfo.switchOn') : $t('wholesaler.ShopInfo.switchOff') }}</span>
          </div>
          <p class="field-hint">{{ $t('wholesaler.ShopInfo.line172_text') }}</p>
        </div>
      </div>

      <!-- 银行收款账户 -->
      <div class="info-section bank-section">
        <h3>{{ $t('wholesaler.ShopInfo.line178_text') }}</h3>
        <p class="section-hint">{{ $t('wholesaler.ShopInfo.line179_text') }}</p>
        
        <div class="form-item">
          <label>{{ $t('wholesaler.ShopInfo.line182_text') }}</label>
          <input 
            type="text" 
            v-model="formData.bankName" 
            :placeholder="$t('wholesaler.ShopInfo.line186_placeholder')"
          />
        </div>

        <div class="form-item">
          <label>{{ $t('wholesaler.ShopInfo.line191_text') }}</label>
          <input 
            type="text" 
            v-model="formData.bankAccountName" 
            :placeholder="$t('wholesaler.ShopInfo.line195_placeholder')"
          />
        </div>

        <div class="form-item">
          <label>{{ $t('wholesaler.ShopInfo.line200_text') }}</label>
          <input 
            type="text" 
            v-model="formData.bankAccountNumber" 
            :placeholder="$t('wholesaler.ShopInfo.line204_placeholder')"
          />
        </div>
      </div>

      <!-- 保存按钮 -->
      <div class="save-section">
        <button class="btn-save" @click="saveShopInfo">
          {{ isEditing ? $t('wholesaler.ShopInfo.updateInfo') : $t('wholesaler.ShopInfo.saveInfo') }}
        </button>
      </div>
    </div>

    <!-- Toast 提示 -->
    <div v-if="showToast" class="toast">
      {{ toastMessage }}
    </div>

    <!-- 地图选点组件 -->
    <MapPicker
      v-if="showMapPicker"
      @confirm="onMapConfirmed"
      @cancel="showMapPicker = false"
    />
  </div>
</template>

<script>
import api, { uploadFile } from '@/services/api';
import { getWholesalerId } from '@/utils/wholesaler';
import MapPicker from '@/components/MapPicker.vue';
import { getImageUrl } from '@/utils/image';

export default {
  name: 'ShopInfo',
  components: { MapPicker },
  data() {
    return {
      isEditing: false,
      showToast: false,
      toastMessage: '',
      showMapPicker: false,
      // ⭐ 数据结构与后端 Wholesaler.java 完全一致
      formData: {
        companyName: '',
        description: '',
        avatarUrl: '',
        contactPerson: '',
        contactPhone: '',
        whatsappNumber: '',
        address: '',
        latitude: '',
        longitude: '',
        businessLicenseNo: '',
        wholesalerLevel: '',
        paynowQrUrl: '',
        bankName: '',
        bankAccountName: '',
        bankAccountNumber: '',
        allowNegativeStock: false
      }
    };
  },
  
  mounted() {
    this.loadShopInfo();
  },
  
  methods: {
    getImageUrl,
    goBack() {
      this.$router.go(-1);
    },

    onMapConfirmed(location) {
      this.formData.address = location.address;
      this.formData.latitude = location.lat;
      this.formData.longitude = location.lng;
      this.showMapPicker = false;
    },

    async loadShopInfo() {
      try {
        const wholesalerId = getWholesalerId()
        if (!wholesalerId) {
          console.error('❌ 未找到批发商 ID')
          this.showToastMsg(this.$t('wholesaler.ShopInfo.merchantNotFound'))
          return
        }
        
        console.log('🔄 开始加载店铺信息... wholesalerId:', wholesalerId)
        const response = await api.get('/shop/info', { params: { wholesalerId } })
        console.log('📦 API Response:', response)
        
        if (response && Object.keys(response).length > 0) {
          console.log('✅ 找到店铺数据，准备填充表单!');
          
          // ⭐ 直接映射后端返回的字段
          this.formData.companyName = response.companyName || '';
          this.formData.address = response.address || '';
          this.formData.contactPerson = response.contactPerson || '';
          this.formData.contactPhone = response.contactPhone || '';
          this.formData.whatsappNumber = response.whatsappNumber || '';
          this.formData.businessLicenseNo = response.businessLicenseNo || '';
          this.formData.wholesalerLevel = response.wholesalerLevel || '';
          this.formData.description = response.description || '';
          this.formData.bankName = response.bankName || '';
          this.formData.bankAccountName = response.bankAccountName || '';
          this.formData.bankAccountNumber = response.bankAccountNumber || '';
          
          // ✅ 处理头像 URL（如果有的话）
          if (response.avatarUrl) {
            this.formData.avatarUrl = response.avatarUrl;
            console.log('✓ avatarUrl:', response.avatarUrl);
          } else {
            // 如果没有 avatarUrl，尝试从 remark 中提取（兼容旧数据结构）
            if (response.remark && response.remark.includes('|AVATAR:')) {
              const avatarMatch = response.remark.split('|AVATAR:')[1];
              if (avatarMatch) {
                this.formData.avatarUrl = avatarMatch.trim();
                console.log('✓ 从 remark 提取 avatarUrl:', this.formData.avatarUrl);
              }
            }
            
            if (!this.formData.avatarUrl) {
              this.formData.avatarUrl = '';
              console.log('⚠️ 没有找到 avatarUrl');
            }
          }
          
          // ⭐ 处理 PayNow 收款码 URL
          if (response.paynowQrUrl) {
            this.formData.paynowQrUrl = response.paynowQrUrl;
            console.log('✓ paynowQrUrl:', response.paynowQrUrl);
          } else {
            this.formData.paynowQrUrl = '';
            console.log('⚠️ 未找到 PayNow 收款码');
          }
          
          // ⭐ 处理零负库存开关
          if (response.allowNegativeStock !== undefined) {
            this.formData.allowNegativeStock = response.allowNegativeStock;
            console.log('✓ allowNegativeStock:', response.allowNegativeStock);
          } else {
            this.formData.allowNegativeStock = false;
          }
          
          // description 字段
          if (response.description) {
            this.formData.description = response.description;
          } else {
            this.formData.description = '';
          }
          
          // ⭐ 经纬度回填（后端 latitude/longitude）
          if (response.latitude) {
            this.formData.latitude = response.latitude;
          }
          if (response.longitude) {
            this.formData.longitude = response.longitude;
          }
          
          console.log('✅ 字段填充完成:');
          console.log('   companyName:', this.formData.companyName);
          console.log('   contactPerson:', this.formData.contactPerson);
          console.log('   avatarUrl:', this.formData.avatarUrl || '(无)');
          console.log('   isEditing:', true);
          
          this.isEditing = true;
          this.showToastMsg(this.$t('wholesaler.ShopInfo.loaded'));
        } else {
          this.showToastMsg(this.$t('wholesaler.ShopInfo.noShopYet'));
        }
        
      } catch (error) {
        console.error('❌ 加载店铺信息失败:', error);
        
        if (error.message.includes('未授权')) {
          this.showToastMsg(this.$t('wholesaler.ShopInfo.pleaseLogin'));
        } else {
          this.showToastMsg(this.$t('wholesaler.ShopInfo.loadFailed') + '：' + error.message);
        }
      }
    },

    triggerAvatarUpload() {
      if (this.isEditing) {
        this.$refs.avatarInput.click();
      } else {
        this.showToastMsg(this.$t('wholesaler.ShopInfo.saveBasicFirst'));
      }
    },

    async handleAvatarUpload(event) {
      const file = event.target.files[0];
      if (!file) return;

      console.log('🖼️ 开始上传头像:', file.name);
      
      try {
        this.showToastMsg(this.$t('wholesaler.ShopInfo.uploading'));
        
        // ✅ 调用封装好的 uploadFile 方法
        const result = await uploadFile('/files/upload-image', file, (progress) => {
          console.log(`📊 上传进度：${progress}%`);
          this.showToastMsg(`${this.$t('wholesaler.ShopInfo.uploading')}... ${progress}%`);
        });
        
        console.log('📦 上传返回值 (完整对象):', result);
        console.log('   typeof:', typeof result);
        console.log('   keys:', result ? Object.keys(result) : 'no keys');
        console.log('   result.url:', result?.url);
        
        // ⭐ 正确的方式：result 就是 backend 返回的对象 {filename, url}
        if (result && result.url) {
          // ✅ 成功获取上传后的 URL
          const uploadedUrl = result.url;
          
          console.log('✅ 图片上传成功！URL:', uploadedUrl);
          this.formData.avatarUrl = uploadedUrl;
          
          // ✅ 立即自动保存到店铺信息
          this.showToastMsg(this.$t('wholesaler.ShopInfo.avatarUploadSuccess'));
          
          await this.saveShopInfo(true); // true = 静默模式，不跳转
        } else {
          throw new Error('无法从响应中获取 url');
        }
        
      } catch (error) {
        console.error('❌ 头像上传失败:', error);
        this.showToastMsg(error.message || this.$t('wholesaler.ShopInfo.avatarUploadFailed'));
      } finally {
        // 清空文件选择器，允许重复上传同一张图片
        event.target.value = '';
      }
    },

    // ✅ 图片加载失败时的处理
    handleImageError(e) {
      console.error('头像图片加载失败:', e.target.src);
      e.target.style.display = 'none';
      this.showToastMsg(this.$t('wholesaler.ShopInfo.avatarLoadFailed'));
      
      // 2 秒后恢复占位符 UI
      setTimeout(() => {
        if (e.target.parentNode) {
          e.target.parentNode.innerHTML = `
            <div class="avatar-placeholder">
              <span class="avatar-icon">📷</span>
              <p class="upload-text">头像加载失败，请重新上传</p>
            </div>
          `;
        }
      }, 2000);
    },

    // ⭐ PayNow 收款码上传
    triggerPayNowUpload() {
      if (this.isEditing) {
        this.$refs.paynowInput.click();
      } else {
        this.showToastMsg(this.$t('wholesaler.ShopInfo.saveBasicFirstPayNow'));
      }
    },

    async handlePayNowUpload(event) {
      const file = event.target.files[0];
      if (!file) return;

      console.log('📱 开始上传 PayNow 收款码:', file.name);
      
      try {
        this.showToastMsg(this.$t('wholesaler.ShopInfo.uploading'));
        
        const result = await uploadFile('/files/upload-image', file, (progress) => {
          console.log(`📊 上传进度：${progress}%`);
          this.showToastMsg(`${this.$t('wholesaler.ShopInfo.uploading')}... ${progress}%`);
        });
        
        if (result && result.url) {
          const uploadedUrl = result.url;
          
          console.log('✅ PayNow 收款码上传成功！URL:', uploadedUrl);
          this.formData.paynowQrUrl = uploadedUrl;
          
          this.showToastMsg(this.$t('wholesaler.ShopInfo.qrUploadSuccess'));
          
          await this.saveShopInfo(true);
        } else {
          throw new Error('无法从响应中获取 url');
        }
        
      } catch (error) {
        console.error('❌ PayNow 收款码上传失败:', error);
        this.showToastMsg(error.message || this.$t('wholesaler.ShopInfo.qrUploadFailed'));
      } finally {
        event.target.value = '';
      }
    },

    async saveShopInfo(silent = false) {
      try {
        const wholesalerId = getWholesalerId()
        if (!wholesalerId) {
          this.showToastMsg(this.$t('wholesaler.ShopInfo.merchantNotFound'))
          return
        }

        if (!this.formData.companyName || !this.formData.companyName.trim()) {
          if (!silent) {
            this.showToastMsg(this.$t('wholesaler.ShopInfo.pleaseInputCompanyName'));
          }
          return;
        }

        console.log('💾 保存数据:', this.formData);
        
        // ⭐ 传递 wholesalerId 参数
        await api.post('/shop/save', this.formData, { params: { wholesalerId } });
        
        if (!silent) {
          this.showToastMsg(this.$t('wholesaler.ShopInfo.saveSuccess'));
          
          // 延迟后返回上一页
          setTimeout(() => {
            this.goBack();
          }, 1500);
        } else {
          console.log('✅ 静默模式保存成功');
        }
        
      } catch (error) {
        console.error('❌ 保存失败:', error);
        if (!silent) {
          this.showToastMsg(this.$t('wholesaler.ShopInfo.saveFailed') + '：' + error.message);
        }
      }
    },

    showToastMsg(message) {
      this.toastMessage = message;
      this.showToast = true;
      
      setTimeout(() => {
        this.showToast = false;
      }, 2500);
    },

    // ⭐ 切换零负库存下单开关
    async toggleNegativeStock() {
      try {
        const wholesalerId = getWholesalerId();
        if (!wholesalerId) {
          this.showToastMsg(this.$t('wholesaler.ShopInfo.merchantNotFoundShort'));
          return;
        }
        
        const allow = this.formData.allowNegativeStock;
        await api.put(`/wholesalers/${wholesalerId}/allow-negative-stock`, null, { 
          params: { allow } 
        });
        
        this.showToastMsg(allow ? this.$t('wholesaler.ShopInfo.negativeStockOn') : this.$t('wholesaler.ShopInfo.negativeStockOff'));
      } catch (error) {
        console.error('切换零负库存开关失败:', error);
        this.showToastMsg(this.$t('wholesaler.ShopInfo.operationFailed') + '：' + (error.message || this.$t('wholesaler.ShopInfo.pleaseRetry')));
        // 恢复原状态
        this.formData.allowNegativeStock = !this.formData.allowNegativeStock;
      }
    }
  }
};
</script>

<style scoped>
.shop-info-page {
  min-height: 100vh;
  background-color: #f5f5f5;
  display: flex;
  flex-direction: column;
}

.back-header {
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: calc(12px + env(safe-area-inset-top, 0px)) 16px;
  background: #fff;
  border-bottom: 1px solid #e8e8e8;
  position: sticky;
  top: 0;
  z-index: 100;
}

.back-btn {
  width: 36px;
  height: 36px;
  display: flex;
  align-items: center;
  justify-content: center;
  cursor: pointer;
  font-size: 20px;
}

.back-header h1 {
  flex: 1;
  font-size: 18px;
  font-weight: 600;
  text-align: center;
  margin-left: 12px;
}

.page-content {
  flex: 1;
  overflow-y: auto;
  -webkit-overflow-scrolling: touch;
  padding-bottom: 80px;
}

.info-section {
  background: #fff;
  border-radius: 12px;
  padding: 16px;
  margin: 12px;
  box-shadow: 0 2px 8px rgba(0,0,0,0.1);
}

.info-section h3 {
  margin-bottom: 16px;
  font-size: 14px;
  font-weight: 700;
  color: #333;
}

.form-item {
  margin-bottom: 16px;
}

.form-item:last-child {
  margin-bottom: 0;
}

.form-item label {
  display: block;
  font-size: 13px;
  font-weight: 600;
  margin-bottom: 8px;
  color: #333;
}

.address-input-row {
  display: flex;
  gap: 8px;
  align-items: flex-end;
}

.address-input-row input {
  flex: 1;
}

.btn-map-pick {
  background: linear-gradient(135deg, #667eea 0%, #764ba2 100%);
  color: white;
  border: none;
  padding: 10px 14px;
  border-radius: 8px;
  font-size: 13px;
  font-weight: 500;
  cursor: pointer;
  white-space: nowrap;
  transition: all 0.2s;
  flex-shrink: 0;
}

.btn-map-pick:hover {
  opacity: 0.9;
  transform: translateY(-1px);
}

.form-item input,
.form-item textarea {
  width: 100%;
  padding: 10px 12px;
  border: 1px solid #e8e8e8;
  border-radius: 8px;
  font-size: 13px;
  outline: none;
  box-sizing: border-box;
  transition: border-color 0.3s;
}

.form-item input:focus,
.form-item textarea:focus {
  border-color: #ff6b35;
}

.form-item textarea {
  resize: vertical;
  min-height: 80px;
}

.avatar-upload {
  display: flex;
  flex-direction: column;
  align-items: flex-start;
}

.avatar-container {
  width: 100%;
  padding: 12px;
  background-color: #f5f5f5;
  border-radius: 8px;
  text-align: center;
  cursor: pointer;
  transition: background-color 0.3s;
  position: relative;
}

.avatar-container:hover {
  background-color: #e8e8e8;
}

.avatar-container img {
  max-width: 100%;
  max-height: 120px;
  border-radius: 8px;
  object-fit: cover;
  transition: all 0.3s;
}



.avatar-icon {
  font-size: 48px;
  margin-bottom: 8px;
}

.upload-text {
  font-size: 12px;
  color: #999;
}

.field-hint {
  font-size: 11px;
  color: #999;
  margin-top: 4px;
}

.domain-preview {
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: 12px;
  background: #f9fafb;
  border-radius: 8px;
  border: 1px solid #e5e7eb;
}

.domain-full {
  font-size: 14px;
  font-weight: 500;
  color: #10b981;
}

.domain-status-badge {
  font-size: 11px;
  padding: 4px 8px;
  border-radius: 12px;
  font-weight: 500;
}

.domain-status-badge.saved {
  background: #dcfce7;
  color: #059669;
}

.domain-status-badge.unsaved {
  background: #fef3c7;
  color: #d97706;
}

/* ⭐ 开关样式 */
.switch-item {
  display: flex;
  flex-direction: column;
  gap: 8px;
}

.switch-container {
  display: flex;
  align-items: center;
  gap: 12px;
}

.switch {
  position: relative;
  display: inline-block;
  width: 50px;
  height: 28px;
}

.switch input {
  opacity: 0;
  width: 0;
  height: 0;
}

.switch-slider {
  position: absolute;
  cursor: pointer;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background-color: #ccc;
  transition: .4s;
  border-radius: 28px;
}

.switch-slider:before {
  position: absolute;
  content: "";
  height: 22px;
  width: 22px;
  left: 3px;
  bottom: 3px;
  background-color: white;
  transition: .4s;
  border-radius: 50%;
}

input:checked + .switch-slider {
  background-color: #10b981;
}

input:checked + .switch-slider:before {
  transform: translateX(22px);
}

.switch-label {
  font-size: 14px;
  font-weight: 500;
  color: #333;
}

input:checked ~ .switch-label {
  color: #10b981;
}

/* ⭐ PayNow 收款码样式 */
.paynow-section {
  border: 2px solid #38bdf8;
  background: linear-gradient(135deg, #f0f9ff 0%, #e0f2fe 100%);
  
  h3 {
    color: #1e40af;
  }
  
  .section-hint {
    font-size: 12px;
    color: #64748b;
    margin: -8px 0 16px 0;
  }
}

.paynow-upload {
  margin-bottom: 0;
}

.paynow-container {
  width: 100%;
  padding: 20px;
  background-color: #fff;
  border-radius: 12px;
  text-align: center;
  cursor: pointer;
  transition: all 0.3s;
  border: 2px dashed #cbd5e1;
  
  &:hover {
    border-color: #38bdf8;
    background-color: #f8fafc;
  }
  
  .paynow-qr-image {
    max-width: 280px;
    width: 100%;
    height: auto;
    border-radius: 8px;
  }
  
  .paynow-placeholder {
    display: flex;
    flex-direction: column;
    align-items: center;
    justify-content: center;
    padding: 20px;
    
    .paynow-icon {
      font-size: 56px;
      margin-bottom: 12px;
    }
    
    .upload-text {
      font-size: 14px;
      color: #475569;
      font-weight: 500;
      margin-bottom: 4px;
    }
    
    .upload-hint {
      font-size: 12px;
      color: #94a3b8;
    }
  }
}

.save-section {
  padding: 12px;
  background-color: #fff;
  margin: 12px;
  border-radius: 12px;
}

.btn-save {
  width: 100%;
  padding: 12px;
  border: none;
  background-color: #ff6b35;
  color: #fff;
  border-radius: 10px;
  font-size: 14px;
  font-weight: 600;
  cursor: pointer;
  transition: opacity 0.3s;
}

.btn-save:active {
  opacity: 0.9;
}

/* Toast 样式 */
.toast {
  position: fixed;
  bottom: 80px;
  left: 50%;
  transform: translateX(-50%);
  background: rgba(0,0,0,0.8);
  color: #fff;
  padding: 12px 20px;
  border-radius: 20px;
  font-size: 13px;
  z-index: 2000;
  animation: fadeIn 0.3s ease;
}

@keyframes fadeIn {
  from {
    opacity: 0;
  }
  to {
    opacity: 1;
  }
}
</style>
